// src/frontend/data/books/BlockchainFundamentals/data/cap7.js

const CAP7 = {
  id: 7,
  slug: "cap7-dai",
  title: "Capítulo 7 — DAI: stablecoin descentralizada, MakerDAO y Vaults",
  sections: [
    {
      heading: "¿Qué es DAI?",
      text: [
        "DAI es una stablecoin (moneda estable) cuyo objetivo es mantenerse cerca de 1 dólar: 1 DAI ≈ 1 USD.",
        "Es un token (históricamente ERC-20 en Ethereum) muy usado en finanzas descentralizadas (DeFi) para operar con un valor estable sin salir del ecosistema cripto.",
        "La gran diferencia frente a muchas stablecoins populares es su enfoque: DAI busca sostener su valor usando colateral dentro de smart contracts, en lugar de depender únicamente de reservas fiat custodiadas por una empresa."
      ]
    },
    {
      heading: "Por qué DAI existe (problema que resuelve)",
      text: [
        "La volatilidad es uno de los mayores obstáculos para usar cripto como dinero en el día a día.",
        "DAI nace para ofrecer estabilidad dentro de cripto: ahorro, pagos, remesas, préstamos, trading y contabilidad con un valor relativamente constante.",
        "DAI se convirtió en una pieza clave del ecosistema DeFi porque funciona como ‘base estable’ para intercambiar, prestar y ahorrar."
      ]
    },
    {
      heading: "MakerDAO: el protocolo detrás de DAI",
      text: [
        "DAI es emitido por el protocolo Maker (MakerDAO), un conjunto de smart contracts que coordinan la emisión, quema y mecanismos de estabilidad.",
        "MakerDAO funciona como una organización descentralizada (DAO): parámetros críticos del sistema se ajustan mediante gobernanza (históricamente vinculada a MKR).",
        "En términos simples: Maker define las reglas; los contratos las ejecutan; la comunidad (y sus incentivos) mantiene el equilibrio del sistema."
      ]
    },
    {
      heading: "Cómo funciona DAI (visión general)",
      list: [
        "Un usuario deposita un activo como garantía (colateral) en un contrato (Vault).",
        "El protocolo permite generar DAI contra ese colateral.",
        "El usuario puede usar ese DAI (pagar, intercambiar, ahorrar, prestar).",
        "Para cerrar la posición, el usuario devuelve DAI y recupera su colateral (el DAI se quema).",
        "Si el colateral cae demasiado y el ratio se vuelve inseguro, el sistema puede liquidar la posición para cubrir la deuda."
      ]
    },
    {
      heading: "Maker Vaults (antes CDP): la pieza central",
      text: [
        "Un Maker Vault es un contrato donde bloqueas colateral y, a cambio, puedes ‘mintear’ (generar) DAI como deuda.",
        "Piensa en un Vault como una bóveda con reglas: ratio mínimo, comisiones (tasa de estabilidad) y umbral de liquidación.",
        "El usuario controla la posición, pero el protocolo aplica reglas automáticas si el riesgo se dispara."
      ]
    },
    {
      heading: "Sobre-colateralización: por qué debes depositar más de lo que generas",
      text: [
        "DAI se sostiene con sobre-colateralización: depositas más valor del que generas en DAI.",
        "Ejemplo conceptual: si el ratio mínimo es 150%, para generar 100 DAI necesitas colateral con valor de al menos 150 USD.",
        "Esto crea un colchón para absorber caídas del precio del colateral y proteger la solvencia del sistema."
      ]
    },
    {
      heading: "Liquidaciones: lo que debes entender sí o sí",
      text: [
        "Si el colateral baja de valor y tu ratio cae por debajo del mínimo, tu Vault puede liquidarse.",
        "La liquidación vende colateral para cubrir la deuda (DAI generado) y normalmente aplica penalización (liquidation penalty).",
        "En la práctica, el riesgo más grande para un usuario de DAI no es DAI en sí, sino abrir un Vault sin margen de seguridad."
      ]
    },
    {
      heading: "DAI original (SAI) vs DAI Multicolateral (MCD)",
      text: [
        "La primera versión de DAI (conocida como SAI) dependía principalmente de un solo colateral (ETH).",
        "Maker evolucionó a DAI Multicolateral (MCD), permitiendo múltiples tipos de colateral para mejorar estabilidad y resiliencia del sistema.",
        "Con el tiempo, SAI fue quedando obsoleto y el ecosistema migró hacia el modelo multicolateral."
      ]
    },
    {
      heading: "Cómo mantiene DAI su estabilidad",
      text: [
        "DAI busca mantenerse cerca de 1 USD combinando incentivos económicos, parámetros del protocolo y mecanismos de mercado.",
        "Cuando DAI se desvía, el sistema ajusta condiciones (por ejemplo, costos de deuda/estabilidad y liquidez) para empujar el precio hacia el objetivo.",
        "La estabilidad perfecta no existe: DAI puede desviarse, pero el diseño busca que vuelva al rango objetivo."
      ]
    },
    {
      heading: "DAI vs USDT (enfoque centralizado vs descentralizado)",
      text: [
        "USDT es emitido por una entidad central y su estabilidad depende de reservas y gestión corporativa.",
        "DAI busca estabilidad usando colateral y smart contracts, con gobernanza descentralizada.",
        "USDT suele ser más simple para usuarios y tiene enorme liquidez; DAI ofrece una narrativa más descentralizada, pero con mayor complejidad y riesgos técnicos."
      ]
    },
    {
      heading: "Usos principales de DAI en DeFi",
      list: [
        "Ahorro: mantener valor estable dentro de cripto.",
        "Préstamos: pedir prestado o prestar DAI en protocolos DeFi.",
        "Trading: ‘parquear’ ganancias sin salir a fiat.",
        "Liquidez: aportar DAI en pools y AMMs.",
        "Pagos/remesas: transferencias globales con valor estable (según costos de red)."
      ]
    },
    {
      heading: "Riesgos reales (sin marketing)",
      list: [
        "Riesgo de liquidación (si abres Vaults con margen bajo).",
        "Riesgo de smart contracts (fallos, exploits, bugs).",
        "Riesgo de mercado (volatilidad del colateral, shocks de liquidez).",
        "Riesgo operativo (fees altos, congestión, errores de red).",
        "Riesgo de gobernanza (cambios de parámetros que afectan estabilidad)."
      ]
    },
    {
      heading: "Pros de DAI",
      list: [
        "Stablecoin con fuerte integración en DeFi.",
        "Enfoque descentralizado (colateral on-chain + reglas automáticas).",
        "Muy utilizada como colateral y unidad estable en protocolos.",
        "Accesible en flujos on-chain sin KYC (según plataforma)."
      ]
    },
    {
      heading: "Contras de DAI",
      list: [
        "Mayor complejidad que stablecoins centralizadas.",
        "Riesgo de liquidación si usas Vaults sin margen.",
        "Dependencia del ecosistema (comisiones y congestión).",
        "La estabilidad depende de mecanismos y gobernanza: no es ‘riesgo cero’."
      ]
    },
    {
      heading: "Buenas prácticas si vas a usar DAI",
      list: [
        "Si abres un Vault, mantén un ratio de colateral alto (margen de seguridad).",
        "Evita apalancarte sin entender liquidaciones.",
        "Usa montos pequeños al inicio para aprender el flujo completo.",
        "Verifica contratos, plataformas y permisos antes de firmar.",
        "No confundas stablecoin con ‘dinero sin riesgo’: siempre hay riesgo operativo y de sistema."
      ]
    },
    {
      heading: "FAQ rápida",
      list: [
        "¿DAI siempre vale 1 USD? → Busca mantenerse cerca, pero puede desviarse temporalmente.",
        "¿DAI está respaldado por dólares en un banco? → Su enfoque principal es colateral on-chain (según diseño del sistema).",
        "¿Puedo perder dinero usando DAI? → Sí, sobre todo si abres Vaults sin margen y te liquidan, o si hay riesgos de protocolo/mercado.",
        "¿Por qué DAI es importante? → Porque dio un estándar de ‘estabilidad descentralizada’ dentro de DeFi."
      ]
    },
    {
      heading: "Conclusión",
      text: [
        "DAI es una de las stablecoins más importantes por su rol estructural en DeFi.",
        "Su promesa es estabilidad sin depender completamente de un emisor central, usando colateral y reglas programadas.",
        "Pero esa descentralización tiene costo: mayor complejidad y riesgos (especialmente liquidaciones).",
        "Si entiendes DAI, entiendes una base esencial de las finanzas descentralizadas."
      ]
    }
  ]
};

export default CAP7;
